import type { ContactPaymentRequestResult } from '@/types/contact';

export interface ContactPaymentRequestDeliveryNotice {
  tone: 'success' | 'warning' | 'error';
  title: string;
  description?: string;
}

function channelLabel(channel: string): string {
  return channel.toLowerCase() === 'sms' ? 'SMS' : channel.toLowerCase();
}

function joinChannels(channels: string[]): string {
  if (channels.length <= 1) return channels[0] ?? 'the selected channel';
  return `${channels.slice(0, -1).join(', ')} and ${channels.at(-1)}`;
}

function deliveryErrorMessage(error?: string): string {
  switch (error) {
    case 'no_tenant_sender':
      return 'No business SMS number is configured.';
    case 'contact_opted_out':
      return 'The contact has opted out of this channel.';
    case 'missing_recipient':
    case 'missing_phone':
      return 'No recipient phone number was provided.';
    case 'missing_email':
      return 'No recipient email address was provided.';
    default:
      return error
        ? `${error.replaceAll('_', ' ').replace(/\.$/, '')}.`
        : 'The delivery provider did not accept the request.';
  }
}

/**
 * The card-request endpoint can generate a valid link while one or more
 * delivery providers fail. Convert that partial-success response into an
 * operator-facing result instead of claiming every request was sent.
 */
export function getContactPaymentRequestDeliveryNotice(
  result: ContactPaymentRequestResult,
): ContactPaymentRequestDeliveryNotice {
  const deliveries = Object.entries(result.delivery ?? {});
  const succeeded = deliveries
    .filter(([, delivery]) => delivery.success)
    .map(([channel]) => channelLabel(channel));
  const failed = deliveries.filter(([, delivery]) => !delivery.success);

  if (failed.length === 0 && succeeded.length > 0) {
    return {
      tone: 'success',
      title: `Secure card request sent by ${joinChannels(succeeded)}`,
    };
  }

  const failureDescription = failed
    .map(
      ([channel, delivery]) => `${channelLabel(channel)}: ${deliveryErrorMessage(delivery.error)}`,
    )
    .join(' ');

  if (succeeded.length > 0) {
    return {
      tone: 'warning',
      title: `Request sent by ${joinChannels(succeeded)}, but another channel failed`,
      description: `${failureDescription} You can copy the secure link below.`,
    };
  }

  const failedChannels = failed.map(([channel]) => channelLabel(channel));
  return {
    tone: 'error',
    title:
      failedChannels.length > 0
        ? `Secure link generated, but ${joinChannels(failedChannels)} ${failedChannels.length === 1 ? 'was' : 'were'} not sent`
        : 'Secure link generated, but no delivery was confirmed',
    description: failureDescription
      ? `${failureDescription} Copy the secure link and send it manually.`
      : 'Copy the secure link and send it manually.',
  };
}
